/**
 * The single safe gateway to `localStorage`.
 *
 * Direct `localStorage` access is linted against across the frontend
 * (`no-restricted-globals`) so every read and write goes through here. That
 * buys three things uniformly:
 *
 * - **Failure tolerance.** Private-mode quotas and storage-disabled browsers
 *   throw on access. Every helper swallows that: a failed read returns the
 *   fallback, a failed write reports `false`. Persistence is best-effort and
 *   never crashes a feature.
 * - **Namespacing.** Keys are conventionally `rome:`-prefixed; `removeByPrefix`
 *   lets a feature clear its own slice without touching another's.
 * - **One place to evolve.** Cross-tab coordination, encryption, or a storage
 *   swap can land here rather than in a dozen call sites.
 *
 * This is the `localStorage`-only surface today; a `sessionStorage` sibling can
 * follow the same shape when something needs it. This module is the one place
 * exempt from the `no-restricted-globals` localStorage rule.
 */

// Methods
// ----------------------------------------------------------------------------

/**
 * Read a string value.
 *
 * @param key The storage key.
 * @param fallback Returned when the key is absent or storage is unreadable.
 * @returns The stored string, or `fallback`.
 */
export const getItem = (key: string, fallback: string | null = null): string | null => {
	try {
		const value = localStorage.getItem(key);
		return value ?? fallback;
	} catch {
		return fallback;
	}
};

/**
 * Write a string value.
 *
 * @param key The storage key.
 * @param value The string to store.
 * @returns Whether the write succeeded; `false` on quota or disabled storage.
 */
export const setItem = (key: string, value: string): boolean => {
	try {
		localStorage.setItem(key, value);
		return true;
	} catch {
		return false;
	}
};

/** Remove a single key. A failure is swallowed; the key stays as-is. */
const removeItem = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		// Nothing to remove if storage is unavailable.
	}
};

/** List the stored keys narrowed to a prefix, or empty when unreadable. */
const keysWithPrefix = (prefix: string): string[] => {
	try {
		const found: string[] = [];
		for (let index = 0; index < localStorage.length; index += 1) {
			const key = localStorage.key(index);
			if (key !== null && key.startsWith(prefix)) found.push(key);
		}
		return found;
	} catch {
		return [];
	}
};

/**
 * Remove every key sharing a prefix.
 *
 * Lets a feature clear its own namespaced slice without disturbing keys owned
 * by other features.
 *
 * @param prefix The key prefix to clear.
 */
export const removeByPrefix = (prefix: string): void => {
	for (const key of keysWithPrefix(prefix)) removeItem(key);
};
